import Phaser from 'phaser';
import { GAME_CONFIG } from '../config/gameConfig';
import { ENCYCLOPEDIA_DATA_KEY } from '../utils/encyclopedia';
import { OSSAN_PROFILES } from '../data/ossanProfiles';

const COLS = 8;
const ROWS_PER_PAGE = 10;
const ITEMS_PER_PAGE = COLS * ROWS_PER_PAGE;
const CELL_WIDTH = 75;
const CELL_HEIGHT = 100;
const PADDING = 16;

export class EncyclopediaScene extends Phaser.Scene {
  private discoveredIds: Set<number> = new Set();
  private currentPage: number = 0;
  private gridContainer!: Phaser.GameObjects.Container;
  private prevBtn!: Phaser.GameObjects.Text;
  private nextBtn!: Phaser.GameObjects.Text;
  private countText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'EncyclopediaScene' });
  }

  init(): void {
    const ids = this.registry.get(ENCYCLOPEDIA_DATA_KEY) as number[] | undefined;
    this.discoveredIds = new Set(ids ?? []);
    this.currentPage = 0;
  }

  create(): void {
    const { WIDTH, HEIGHT, COLORS, FONTS, TOTAL_ITEM_TYPES } = GAME_CONFIG;

    // 背景
    const bg = this.add.graphics();
    bg.fillStyle(COLORS.WHITE, 1);
    bg.fillRect(0, 0, WIDTH, HEIGHT);

    // ヘッダー
    const header = this.add.text(WIDTH / 2, 50, 'おっさん図鑑', {
      fontFamily: FONTS.DEFAULT,
      fontSize: '36px',
      color: '#000000',
      fontStyle: 'bold',
    });
    header.setOrigin(0.5);

    // 発見数表示
    this.countText = this.add.text(
      WIDTH / 2,
      95,
      `${this.discoveredIds.size} / ${TOTAL_ITEM_TYPES}`,
      {
        fontFamily: FONTS.DEFAULT,
        fontSize: '20px',
        color: '#666666',
      }
    );
    this.countText.setOrigin(0.5);

    // グリッドコンテナ
    const gridY = 130;
    this.gridContainer = this.add.container(0, 0);
    this.createGrid(gridY);

    // ページネーション（2ページ以上の場合）
    const totalPages = Math.ceil(TOTAL_ITEM_TYPES / ITEMS_PER_PAGE);
    if (totalPages > 1) {
      const pageY = gridY + ROWS_PER_PAGE * CELL_HEIGHT + 30;
      this.prevBtn = this.add.text(WIDTH / 2 - 80, pageY, '＜ 前へ', {
        fontFamily: FONTS.DEFAULT,
        fontSize: '20px',
        color: this.currentPage > 0 ? '#e74c3c' : '#999999',
      });
      this.prevBtn.setOrigin(0.5);
      this.prevBtn.setInteractive({ useHandCursor: true });
      this.prevBtn.on('pointerdown', () => this.changePage(-1));

      const pageText = this.add.text(WIDTH / 2, pageY, `${this.currentPage + 1} / ${totalPages}`, {
        fontFamily: FONTS.DEFAULT,
        fontSize: '20px',
        color: '#333333',
      });
      pageText.setOrigin(0.5);
      pageText.setName('pageText');

      this.nextBtn = this.add.text(WIDTH / 2 + 80, pageY, '次へ ＞', {
        fontFamily: FONTS.DEFAULT,
        fontSize: '20px',
        color: this.currentPage < totalPages - 1 ? '#e74c3c' : '#999999',
      });
      this.nextBtn.setOrigin(0.5);
      this.nextBtn.setInteractive({ useHandCursor: true });
      this.nextBtn.on('pointerdown', () => this.changePage(1));
    }

    // とじるボタン
    const closeBtn = this.add.text(WIDTH / 2, HEIGHT - 60, 'とじる', {
      fontFamily: FONTS.DEFAULT,
      fontSize: `${FONTS.MEDIUM_SIZE}px`,
      color: '#e74c3c',
      fontStyle: 'bold',
    });
    closeBtn.setOrigin(0.5);
    closeBtn.setInteractive({ useHandCursor: true });
    closeBtn.on('pointerdown', () => {
      this.sound.play('button');
      this.scene.start('TitleScene');
    });
  }

  private createGrid(startY: number): void {
    const { WIDTH, TOTAL_ITEM_TYPES } = GAME_CONFIG;

    this.gridContainer.removeAll(true);

    const startId = this.currentPage * ITEMS_PER_PAGE + 1;
    const gridWidth = COLS * CELL_WIDTH;
    const offsetX = (WIDTH - gridWidth) / 2 + CELL_WIDTH / 2 + PADDING / 2;

    for (let i = 0; i < ITEMS_PER_PAGE; i++) {
      const itemId = startId + i;
      if (itemId > TOTAL_ITEM_TYPES) break;

      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const x = offsetX + col * CELL_WIDTH;
      const y = startY + row * CELL_HEIGHT + CELL_HEIGHT / 2;

      const discovered = this.discoveredIds.has(itemId);

      if (discovered) {
        const img = this.add.image(x, y, `item_${itemId}`);
        img.setDisplaySize(CELL_WIDTH, CELL_HEIGHT);
        img.setInteractive({ useHandCursor: true });
        img.on('pointerdown', () => this.showItemModal(itemId));
        this.gridContainer.add(img);
      } else {
        // 未発見は「?」表示
        const cellBg = this.add.rectangle(x, y, CELL_WIDTH, CELL_HEIGHT, 0xeeeeee, 1);
        cellBg.setStrokeStyle(2, 0xcccccc);
        this.gridContainer.add(cellBg);

        const qText = this.add.text(x, y, '?', {
          fontFamily: GAME_CONFIG.FONTS.DEFAULT,
          fontSize: '36px',
          color: '#999999',
        });
        qText.setOrigin(0.5);
        this.gridContainer.add(qText);
      }
    }
  }

  private changePage(delta: number): void {
    const totalPages = Math.ceil(GAME_CONFIG.TOTAL_ITEM_TYPES / ITEMS_PER_PAGE);
    const newPage = Phaser.Math.Clamp(this.currentPage + delta, 0, totalPages - 1);
    if (newPage === this.currentPage) return;

    this.currentPage = newPage;
    this.sound.play('button');

    // グリッド再描画
    this.createGrid(130);

    // ページ表示更新
    const pageText = this.children.getByName('pageText') as Phaser.GameObjects.Text;
    if (pageText) {
      pageText.setText(`${this.currentPage + 1} / ${totalPages}`);
    }

    // 前へ・次へボタンの色を更新
    if (this.prevBtn) {
      this.prevBtn.setColor(this.currentPage > 0 ? '#e74c3c' : '#999999');
    }
    if (this.nextBtn) {
      this.nextBtn.setColor(this.currentPage < totalPages - 1 ? '#e74c3c' : '#999999');
    }
  }

  private showItemModal(itemId: number): void {
    const { WIDTH, HEIGHT, COLORS, FONTS } = GAME_CONFIG;

    this.sound.play('button');

    const profileIndex = Math.min(itemId - 1, OSSAN_PROFILES.length - 1);
    const p = OSSAN_PROFILES[profileIndex] ?? {
      name: `おっさん No.${itemId}`,
      age: 45,
      job: '不明',
      family: '不明',
      profile: '謎のおっさん。',
    };

    // 背景オーバーレイ
    const overlay = this.add.rectangle(
      WIDTH / 2,
      HEIGHT / 2,
      WIDTH,
      HEIGHT,
      0x000000,
      0.7
    );
    overlay.setDepth(200);

    // モーダル背景（プロフィールがはみ出ないよう高さ・レイアウト調整）
    const modalW = 420;
    const modalH = 680;
    const textPadding = 24;
    const textWidth = modalW - textPadding * 2;
    const modalBg = this.add.graphics();
    modalBg.fillStyle(COLORS.WHITE, 1);
    modalBg.fillRect(-modalW / 2, -modalH / 2, modalW, modalH);
    modalBg.lineStyle(4, COLORS.BLACK, 1);
    modalBg.strokeRect(-modalW / 2, -modalH / 2, modalW, modalH);

    // 画像（大きく表示）
    const img = this.add.image(0, -200, `item_${itemId}`);
    img.setDisplaySize(160, 213);
    img.setOrigin(0.5);

    // 名前
    const nameText = this.add.text(0, 30, p.name, {
      fontFamily: FONTS.DEFAULT,
      fontSize: '30px',
      color: '#000000',
      fontStyle: 'bold',
    });
    nameText.setOrigin(0.5);

    // 年齢・職業・家族構成（折り返し対応）
    const ageStr = `${p.age}歳 ／ ${p.job ?? '不明'} ／ ${p.family}`;
    const ageText = this.add.text(0, 65, ageStr, {
      fontFamily: FONTS.DEFAULT,
      fontSize: '18px',
      color: '#555555',
      wordWrap: { width: textWidth, useAdvancedWrap: true },
      align: 'center',
      lineSpacing: 2,
    });
    ageText.setOrigin(0.5, 0);

    // 年齢テキストの折り返しに応じてプロフィール開始位置を計算（プロフィール上に1行分の余白）
    const ageLineHeight = 20;
    const ageLines = Math.min(Math.ceil(ageStr.length / 22), 3) || 1;
    const profileStartY = 65 + ageLines * ageLineHeight + 10 + ageLineHeight;

    // プロフィール（折り返し表示、モーダル内に収める）
    const profileText = this.add.text(0, profileStartY, p.profile, {
      fontFamily: FONTS.DEFAULT,
      fontSize: '17px',
      color: '#333333',
      wordWrap: { width: textWidth, useAdvancedWrap: true },
      align: 'center',
      lineSpacing: 4,
    });
    profileText.setOrigin(0.5, 0);

    // プロフィール領域をマスクでクリップ（はみ出し防止）
    const maskShape = this.make.graphics({});
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(-modalW / 2 + textPadding, profileStartY - 5, modalW - textPadding * 2, 200);
    maskShape.setVisible(false);
    const mask = maskShape.createGeometryMask();
    profileText.setMask(mask);

    // とじるボタン（プロフィール下に余白を持たせて少し上に配置）
    const closeBtn = this.add.text(0, 285, 'とじる', {
      fontFamily: FONTS.DEFAULT,
      fontSize: `${FONTS.SMALL_SIZE}px`,
      color: '#e74c3c',
      fontStyle: 'bold',
    });
    closeBtn.setOrigin(0.5);
    closeBtn.setInteractive({ useHandCursor: true });

    const modal = this.add.container(WIDTH / 2, HEIGHT / 2, [
      modalBg,
      img,
      nameText,
      ageText,
      profileText,
      closeBtn,
    ]);
    // マスク用Graphicsをシーンに追加（非表示）
    maskShape.setPosition(WIDTH / 2, HEIGHT / 2);
    this.add.existing(maskShape);
    modal.setDepth(201);

    const closeModal = () => {
      this.sound.play('button');
      overlay.destroy();
      modal.destroy();
    };

    closeBtn.on('pointerdown', closeModal);
    overlay.setInteractive();
    overlay.on('pointerdown', closeModal);
  }
}
